/**
 * Mark Complete Overlay Button Handler
 *
 * The confirmation modal (#completeConfirmModal) can be opened by several paths, including a
 * projects-table badge that uses Bootstrap's declarative data-bs-toggle="modal". That
 * declarative open does NOT run the .badge.clickable JS handler in the dashboard scripts
 * (which is a direct binding that misses dynamically-rendered table rows), so on that path:
 *   1. the footer buttons stay display:none (CSS default) and the spinner spins forever;
 *   2. the score-calculation AJAX (fetch_project_data / get_quality_data_by_year) never fires,
 *      so the finalized project has no maturity score; and
 *   3. #markCompleteButton never receives the project id / framework, so finalizing fails.
 *
 * We fix all three by binding to the modal's own (delegated) show.bs.modal event:
 *   - transfer the triggering element's data onto the finalize button + WebAssessmentCharts;
 *   - run the same score calculation the dashboard/kanban paths run (this fires the AJAX);
 *   - keep the spinner up while it runs and reveal the footer when the score is ready;
 *   - a failsafe timeout always reveals the footer so the spinner can never hang forever.
 */

(function($) {
	'use strict';

	var BUILD = '[mark-complete-overlay v6]';
	console.log('mark-complete-overlay.js loaded ' + BUILD);

	function extractPostIdFromBodyClass() {
		var bodyClasses = document.body.className.split(' ');
		for (var i = 0; i < bodyClasses.length; i++) {
			var match = bodyClasses[i].match(/^postid-(\d+)$/);
			if (match) { return match[1]; }
		}
		return null;
	}

	// Footer buttons are display:none by default (style.css); reveal them, hide the spinner.
	function revealFooter($modal) {
		$modal.find('.spinner-border').hide();
		$modal.find('.mark-complete-modal-buttons').show();
		$modal.find('#markCompleteButton').show();
	}

	function showSpinner($modal) {
		$modal.find('.mark-complete-modal-buttons').hide();
		$modal.find('#markCompleteButton').hide();
		$modal.find('.spinner-border').show();
	}

	// Pull a numeric overall-maturity value out of whatever a calc helper returns.
	function extractScore(score) {
		if (score && typeof score === 'object' && score.hasOwnProperty('overallMaturity')) {
			return score.overallMaturity;
		}
		if (typeof score === 'number') { return score; }
		return null;
	}

	// Average the numeric values of a MaturityScoresOverallXXXX object (CRF-S frameworks).
	function averageScoresObject(obj) {
		if (!obj || typeof obj !== 'object') { return null; }
		var vals = Object.keys(obj).map(function(k) { return obj[k]; })
			.filter(function(v) { return typeof v === 'number' && !isNaN(v); });
		if (!vals.length) { return null; }
		var sum = vals.reduce(function(a, b) { return a + b; }, 0);
		return sum / vals.length;
	}

	function crfsScore(WAC, slug) {
		if (typeof WAC.crfMaturityLevelScoresDispatcher === 'function') {
			WAC.crfMaturityLevelScoresDispatcher(slug);
		}
		var byslug = {
			'crf-s-2021': WAC.MaturityScoresOverall2021,
			'crf-s-2022': WAC.MaturityScoresOverall2022,
			'crf-s-2023': WAC.MaturityScoresOverall2023,
			'crf-s': WAC.MaturityScoresOverall2024,
			'crf-s-2024': WAC.MaturityScoresOverall2024,
			'crf-s-2025': WAC.MaturityScoresOverall2025,
			'crf-s-2026': WAC.MaturityScoresOverall2026,
			'crf-s-appsec-2026': WAC.MaturityScoresOverall2026,
			'crf-s-gov-2026': WAC.MaturityScoresOverall2026,
			'crf-s-hyg-2026': WAC.MaturityScoresOverall2026,
			'crf-s-smb-2026': WAC.MaturityScoresOverall2026
		};
		return averageScoresObject(byslug[slug]);
	}

	function nycrrScore(WAC) {
		var items = WAC.processedItems || [];
		var cat = {};
		items.forEach(function(item) {
			if (!item || !item.safeguardid) { return; }
			var m = String(item.safeguardid.S || '').match(/^500\.(\d+)/);
			if (!m) { return; }
			if (item.maturityscore == null) { return; }
			var c = m[1];
			if (!cat[c]) { cat[c] = { total: 0, count: 0 }; }
			cat[c].total += item.maturityscore;
			cat[c].count++;
		});
		var avgs = [];
		Object.keys(cat).forEach(function(c) {
			if (cat[c].count > 0) { avgs.push((cat[c].total / cat[c].count) * 5); }
		});
		if (!avgs.length) { return null; }
		return avgs.reduce(function(a, b) { return a + b; }, 0) / avgs.length;
	}

	// Compute the overall maturity score for the framework. Returns Promise<number|null>.
	// Mirrors the per-framework logic used by the dashboard/kanban Finalize flows.
	function computeMaturityScore(WAC, frameworkSlug) {
		try {
			if (frameworkSlug.indexOf('cis-') === 0 && typeof WAC.calculateCISAverages === 'function') {
				return Promise.resolve(WAC.calculateCISAverages(WAC.processedItems)).then(extractScore);
			}
			if (frameworkSlug.indexOf('nist-csf-') === 0 && typeof WAC.calculateNIST2Averages === 'function') {
				return Promise.resolve(WAC.calculateNIST2Averages(WAC.processedItems)).then(extractScore);
			}
			if (frameworkSlug.indexOf('nycrr-') === 0) {
				return Promise.resolve(nycrrScore(WAC));
			}
			if (frameworkSlug.indexOf('crf-s') === 0) {
				return Promise.resolve(crfsScore(WAC, frameworkSlug));
			}
		} catch (err) {
			console.warn(BUILD + ' computeMaturityScore error:', err);
		}
		return Promise.resolve(null);
	}

	$(document).ready(function() {
		console.log('mark-complete-overlay.js document ready ' + BUILD);

		// =====================================================================
		// Reveal + wire up the finalize modal whenever it is shown, regardless of
		// which code path opened it. Delegated on document so it works even if the
		// modal / trigger are injected after page load.
		// =====================================================================
		$(document).on('show.bs.modal', '#completeConfirmModal', function(e) {
			var $modal = $(this);

			// Bootstrap sets relatedTarget on the native event; jQuery may not copy it
			// onto its wrapper for this custom event type, so check originalEvent too.
			var trigger = e.relatedTarget || (e.originalEvent && e.originalEvent.relatedTarget);

			if (!trigger) {
				// Opened programmatically (kanban / status-modal / overlay-button paths):
				// those set up the button + run their own calc, so just make sure the
				// footer is usable and leave their data/flow alone.
				revealFooter($modal);
				return;
			}

			var $t = $(trigger);
			var projectId     = $t.attr('data-project-id')     || $t.data('project-id');
			var projectSlug   = $t.attr('data-project-slug')   || $t.data('project-slug');
			var frameworkId   = $t.attr('data-framework-id')   || $t.data('framework-id');
			var frameworkSlug = $t.attr('data-framework-slug') || $t.data('framework-slug');

			// Transfer the project id onto the finalize button so finalizing works.
			var $mcb = $modal.find('#markCompleteButton');
			if (projectId) {
				$mcb.attr('data-project-id', projectId).data('project-id', projectId);
			}

			var WAC = window.WebAssessmentCharts;
			if (WAC) {
				if (frameworkId)   { WAC.frameworkId   = frameworkId; }
				if (frameworkSlug) { WAC.frameworkSlug = frameworkSlug; }
				WAC.calculatedOverallMaturityScore = null;
			}

			var canCalc = WAC &&
				typeof WAC.fetchDataAndCalculateScoresDispatcher === 'function' &&
				frameworkSlug && projectSlug;

			if (!canCalc) {
				// Nothing to compute here — make the modal usable. Finalize will fall
				// back to whatever score sources updateProjectCompletedField can find.
				console.log(BUILD + ' modal wired (no calc available): projectId=' + projectId + ' frameworkSlug=' + frameworkSlug);
				revealFooter($modal);
				return;
			}

			// Show the spinner while we fetch + score, so the finalized project stores
			// its maturity score and the user can't finalize before it is ready.
			console.log(BUILD + ' modal wired, calculating score: projectId=' + projectId + ' frameworkSlug=' + frameworkSlug);
			showSpinner($modal);

			var done = false;
			function finish(reason) {
				if (done) { return; }
				done = true;
				console.log(BUILD + ' revealing footer (' + reason + '); calculatedOverallMaturityScore=' +
					(WAC ? WAC.calculatedOverallMaturityScore : 'n/a'));
				revealFooter($modal);
			}

			// Failsafe: never let the spinner hang forever, even if the AJAX stalls.
			var failsafe = setTimeout(function() { finish('timeout'); }, 12000);

			Promise.resolve()
				.then(function() { return WAC.fetchDataAndCalculateScoresDispatcher(projectSlug, frameworkSlug); })
				.then(function() {
					// fetchDataAndCalculateScores reveals the footer itself (global
					// selectors) as soon as the dashboard render finishes, i.e. before the
					// maturity score below is computed. Re-hide it here so the buttons stay
					// hidden until the score is actually ready. This runs in a microtask
					// (before the next paint), so there is no visible flash.
					showSpinner($modal);
					return computeMaturityScore(WAC, frameworkSlug);
				})
				.then(function(score) {
					if (typeof score === 'number' && !isNaN(score)) {
						WAC.calculatedOverallMaturityScore = score;
					}
				})
				.catch(function(err) { console.warn(BUILD + ' score calculation failed:', err); })
				.then(function() { clearTimeout(failsafe); finish('calc-complete'); });
		});

		// Adjust backdrop z-index and disable animation when the modal is shown.
		$(document).on('show.bs.modal', '#completeConfirmModal', function() {
			setTimeout(function() {
				var backdrops = document.querySelectorAll('.modal-backdrop');
				if (backdrops.length > 0) {
					var lastBackdrop = backdrops[backdrops.length - 1];
					lastBackdrop.style.zIndex = '1059';
					lastBackdrop.style.transition = 'none';
					lastBackdrop.style.opacity = '0.5';
				}
			}, 0);
		});

		// Overlay Mark Complete button path (renders a #markProjectCompleteBtn / .btn-mark-complete
		// button rather than a declarative data-bs-toggle badge).
		$(document).on('click', '#markProjectCompleteBtn, .btn-mark-complete', function(e) {
			e.preventDefault();
			e.stopPropagation();

			var $btn = $(this);
			var projectId = $btn.data('project-id') || $btn.attr('data-project-id');
			if (!projectId) {
				projectId = (typeof getPostIdFromBodyClass === 'function')
					? getPostIdFromBodyClass()
					: extractPostIdFromBodyClass();
			}
			if (!projectId) {
				console.error('Could not determine project ID');
				return;
			}

			$('#markCompleteButton').data('project-id', projectId).attr('data-project-id', projectId);

			var modal = document.getElementById('completeConfirmModal');
			if (modal) {
				// Pass the button as relatedTarget so the show.bs.modal handler can read
				// any framework data attributes off it.
				var bsModal = bootstrap.Modal.getInstance(modal) || new bootstrap.Modal(modal);
				bsModal.show($btn.get(0));
				console.log(BUILD + ' Showing completeConfirmModal (overlay button path)');
			} else {
				console.error('completeConfirmModal not found');
			}
		});

		// Finalize (Mark Complete) button inside the modal.
		$(document).on('click', '#markCompleteButton', function(e) {
			e.preventDefault();

			var $button = $(this);
			var projectId = $button.data('project-id') || $button.attr('data-project-id');

			console.log(BUILD + ' Finalize Data confirmed for project ID:', projectId);

			if (!projectId) {
				console.error('No project ID found on markCompleteButton');
				return;
			}

			// Guard against multiple handlers double-submitting on the same click.
			if ($button.data('mc-submitting')) { return; }
			$button.data('mc-submitting', true);

			$button.attr('disabled', 'disabled').text('Processing...');

			if (typeof updateProjectCompletedField === 'function') {
				updateProjectCompletedField(projectId);
			} else {
				console.error('updateProjectCompletedField function not found');
				$button.removeAttr('disabled').text('Mark as Complete').data('mc-submitting', false);
			}
		});
	});

})(jQuery);
